import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import { ReactElement } from 'react'
import { SectionTitle } from 'app/components/ai-agent-voice-ops/SectionTitle'
import { StatsRow } from 'app/components/ai-agent-voice-ops/StatsRow'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { AiFunnel } from './AiFunnel'
import { AiTurnDrawer } from './AiTurnDrawer'
import { DataSourceBadge } from './DataSourceBadge'
import { ListErrorState } from './ListErrorState'
import type { UseSmsAgentOpsReturn } from './useSmsAgentOps'

type AiQualitySectionProps = Pick<
  UseSmsAgentOpsReturn,
  | `listErrors`
  | `isLoading`
  | `qualityStats`
  | `funnelStages`
  | `toolErrorTurns`
  | `aiTurnCount`
  | `toolErrorColumns`
  | `toolErrorListRef`
  | `selectedTurn`
  | `selectedTurnLocationName`
  | `closeTurn`
  | `navigate`
>

/** How well the AI agent texts (plan D15): speed, tool errors and cost, the funnel, and the live tool error list. */
export function AiQualitySection({
  listErrors,
  isLoading,
  qualityStats,
  funnelStages,
  toolErrorTurns,
  aiTurnCount,
  toolErrorColumns,
  toolErrorListRef,
  selectedTurn,
  selectedTurnLocationName,
  closeTurn,
  navigate,
}: AiQualitySectionProps): ReactElement {
  const scannedNote =
    aiTurnCount === undefined ? `` : `, out of the ${aiTurnCount.toLocaleString(`en-US`)} most recent AI replies`

  return (
    <>
      <div className="mb-8">
        <div className="mb-3">
          <SectionTitle title="AI Quality" adornment={<DataSourceBadge source="preview" />} />
        </div>
        <StatsRow stats={qualityStats} isLoading={isLoading} skeletonCount={4} />
      </div>

      <div className="mb-8">
        <div className="mb-1">
          <SectionTitle title="AI Funnel" adornment={<DataSourceBadge source="preview" />} />
        </div>
        <p className="mb-3 text-body-sm text-subtle">How far AI conversations get: the patient replies, then books.</p>
        <AiFunnel stages={funnelStages} isLoading={isLoading} />
      </div>

      <div ref={toolErrorListRef} className="mb-8 scroll-mt-4">
        <div className="mb-1">
          <SectionTitle
            title="Tool Errors"
            badge={`${toolErrorTurns?.length ?? 0} Replies`}
            adornment={<DataSourceBadge source="live" />}
          />
        </div>
        <p className="mb-3 text-body-sm text-subtle">
          {`AI replies where a tool returned an error${scannedNote}. Open a row to see every trace on its thread.`}
        </p>
        <SimpleTable
          activeSubpage="sms-agent-ops-tool-errors"
          data={toolErrorTurns}
          columns={toolErrorColumns}
          enableDeepLinking={false}
          hideFooter
          meta={{ getRowStyle: (row) => (row?.id === selectedTurn?.id ? `table-selected-row` : ``) }}
          emptyState={
            listErrors.aiTurns ? (
              <ListErrorState subject="AI replies" />
            ) : (
              <TableEmptyState
                icon={<WrenchScrewdriverIcon className="h-6 w-6" />}
                title="No tool errors"
                description="AI replies where a tool the agent called returned an error show up here."
              />
            )
          }
        />
      </div>

      <AiTurnDrawer
        turn={selectedTurn}
        locationName={selectedTurnLocationName}
        onClose={closeTurn}
        onOpenThread={navigate}
      />
    </>
  )
}
